/**
 * Lettura dell'envelope di errore dell'API.
 *
 * L'API risponde `{ "error": "<messaggio>" }`, e quando il rifiuto ha un motivo che il client deve
 * poter riconoscere aggiunge `{ "code": "<CODICE>" }` come campo fratello. Il codice è il
 * discriminante stabile: il messaggio può cambiare o essere tradotto lato server.
 *
 * Senza questo, un fallimento HTTP arriva ai componenti come `HttpErrorResponse`, il cui `.message`
 * è la stringa di Angular ("Http failure response for ...: 409 Conflict"): i gestori che fanno
 * `error?.message` la mostrerebbero così com'è all'utente.
 */

/** L'operazione non è possibile perché l'oggetto appartiene a un'entity sandbox (HTTP 409). */
export const SANDBOX_ERROR_CODE = 'SANDBOX_ENVIRONMENT';

export interface ApiError extends Error {
  status: number | null;
  code: string | null;
}

interface ApiErrorEnvelope {
  error?: string | { message?: string } | null;
  code?: string | null;
  message?: string | null;
}

function readEnvelope(error: any): ApiErrorEnvelope | null {
  const body = error?.error;
  if (body && typeof body === 'object') {
    return body as ApiErrorEnvelope;
  }
  return null;
}

/** Messaggio dell'API, se l'envelope ne porta uno riconoscibile. */
export function readApiErrorMessage(error: any): string | null {
  const body = error?.error;
  if (typeof body === 'string') {
    return body || null;
  }
  const envelope = readEnvelope(error);
  if (!envelope) {
    return null;
  }
  if (typeof envelope.error === 'string') {
    return envelope.error || null;
  }
  if (envelope.error && typeof envelope.error === 'object' && typeof envelope.error.message === 'string') {
    return envelope.error.message || null;
  }
  return typeof envelope.message === 'string' ? envelope.message || null : null;
}

/**
 * Codice applicativo del rifiuto, quando l'API lo espone. Accetta sia un fallimento HTTP grezzo
 * (codice dentro l'envelope) sia un `ApiError` già normalizzato (codice in cima).
 */
export function readApiErrorCode(error: any): string | null {
  if (typeof error?.code === 'string' && error.code) {
    return error.code;
  }
  return readEnvelope(error)?.code ?? null;
}

export function isSandboxError(error: any): boolean {
  return readApiErrorCode(error) === SANDBOX_ERROR_CODE;
}

/**
 * Normalizza un fallimento (HTTP o `Error` già lanciato a monte) in un `ApiError` con un messaggio
 * mostrabile.
 *
 * `fallbackMessage` di default è vuoto, non una frase inglese: i chiamanti mostrano
 * `error?.message || this.translate.instant('...')`, quindi un fallback qui dentro ruberebbe il
 * posto alla stringa tradotta del componente. Il messaggio dell'API arriva solo quando c'è davvero.
 */
export function toApiError(error: any, fallbackMessage = ''): ApiError {
  const message = readApiErrorMessage(error)
    || (error instanceof Error && !isHttpFailure(error) ? error.message : '')
    || fallbackMessage;

  const apiError = new Error(message) as ApiError;
  apiError.status = typeof error?.status === 'number' ? error.status : null;
  apiError.code = readApiErrorCode(error);
  return apiError;
}

// HttpErrorResponse è un Error il cui message è la stringa generata da Angular: non va mostrata.
function isHttpFailure(error: any): boolean {
  return typeof error?.status === 'number' && 'error' in error;
}
